/**
 * Everything BOTH interior editing surfaces need, derived once.
 *
 * The plan view (issue 132) and the 3D drag (issue 133) edit the same
 * `interiorLayout` through the same `resolvePlanDrag` pipeline. What they each
 * need first is the room rectangle, the heater footprint, the doors and the
 * resolved opening set — and all four are the ENGINE's, read back rather than
 * recomputed (see `interiorLayoutResolver`).
 *
 * This module exists so that derivation happens in ONE function instead of once
 * per surface. Two copies of the same four calls would still be two copies: the
 * moment one of them learned about a new awkward case (a panorama moving the
 * heater, a two-room hanging its benches on the front wall) the other surface
 * would measure a different room, and the BOM would depend on where you dragged.
 */
import type {
	InteriorDoor,
	InteriorLayout,
	InteriorRoom,
} from "../../domain/interior";
import type { OpeningLike } from "../../domain/openings/validateOpeningSet";
import {
	deriveInteriorDoors,
	deriveInteriorLayoutContext,
	type InteriorLayoutContext,
	interiorRooms,
	resolveInteriorLayout,
} from "../../engines/interiorLayoutResolver";
import type { BuildInput } from "../../engines/types";
import type { PlanDragContext } from "./PlanView/planDragModel";
import { resolvePlanOpenings } from "./PlanView/planOpenings";

export interface InteriorEditModel {
	layout: InteriorLayout;
	context: InteriorLayoutContext;
	doors: InteriorDoor[];
	relaxDoors: InteriorDoor[];
	/** The openings the engine BUILDS — see `planOpenings.ts`. */
	openings: OpeningLike[];
	/** Every room the layout may place runs in, hot room first. */
	rooms: InteriorRoom[];
	/**
	 * The context `resolvePlanDrag` takes. Assembled here so the two surfaces
	 * cannot validate against different inputs — notably `ceilingHeightMm`, which
	 * is deliberately omitted (an absent height means "skip the headroom rule",
	 * and a surface that supplied one would refuse drags the other allows).
	 */
	dragContext: PlanDragContext;
}

/**
 * Derive the editing model for a build input, or null for a design with no
 * layout-driven interior (today: the deprecated barrel, which keeps its
 * generated one).
 *
 * Returns null rather than throwing on a half-built input: this feeds an
 * OPTIONAL viewer affordance, and a throw here would take the whole 3D view
 * down with it (cf. the plan-drawing PDF regression in the 2026-07-30 note).
 */
export function deriveInteriorEditModel(
	input: BuildInput,
): InteriorEditModel | null {
	if (!input.thermalInputs) return null;

	const context = deriveInteriorLayoutContext(input);
	if (!context) return null;
	const layout = resolveInteriorLayout(input, context);
	if (!layout) return null;

	const doors = deriveInteriorDoors(input, context.room, context.envelope);
	const relaxDoors = context.relaxRoom
		? deriveInteriorDoors(input, context.relaxRoom, context.envelope)
		: [];

	return {
		layout,
		context,
		doors,
		relaxDoors,
		openings: resolvePlanOpenings(input),
		rooms: interiorRooms(context),
		dragContext: {
			room: context.room,
			relaxRoom: context.relaxRoom,
			heaterSize: context.heaterSize,
			heaterType: input.thermalInputs.heaterType,
			heaterModelId: input.thermalInputs.heaterModelId,
			dimensions: input.dimensions,
			doors,
			relaxDoors,
			overrides: input.overrides,
			market: input.market ?? "se",
		},
	};
}
